import React from "react";
import { connect } from "react-redux";

import { faLink } from "@fortawesome/pro-solid-svg-icons/faLink";
import { defineMessages, useIntl } from "react-intl";

import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import { primaryEntityComparator } from "@mapmycustomers/shared/util/comparator";

import Section from "@app/component/activity/ActivityAnnotation/Section";
import { doesOrganizationSupportMultiContactActivities } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import getActivityPeople from "@app/util/activity/getActivityPeople";

import EntityLink from "../EntityLink";

import styles from "./RelationshipsSection.module.scss";

const MAX_VISIBLE_PEOPLE = 5;

const messages = defineMessages({
  morePeople: {
    id: "component.activityAnnotation.relationshipsSection.morePeople",
    defaultMessage: "+ {count} more",
    description: "Static indicator showing how many people are hidden past the visible cap",
  },
});

interface RelationshipsSectionProps {
  activity: Activity;
}

const RelationShipsSection = ({
  activity,
  isMultiContactEnabled,
}: RelationshipsSectionProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const people = getActivityPeople(activity);

  if (!activity.account && !people.length && !activity.deal) {
    return null;
  }

  // Primary is a multi-contact concept; don't flag a primary person when the add-on is off.
  const primaryId = isMultiContactEnabled ? activity.contact?.id : undefined;
  const sortedPeople =
    people.length > 1 ? [...people].sort(primaryEntityComparator(primaryId)) : people;
  const visiblePeople = sortedPeople.slice(0, MAX_VISIBLE_PEOPLE);
  const hiddenCount = sortedPeople.length - visiblePeople.length;

  return (
    <Section icon={faLink} title={"Relationships"}>
      <EntityLink entity={activity.account} entityType={EntityType.COMPANY} />
      {visiblePeople.map((person) => (
        <EntityLink
          entity={person}
          entityType={EntityType.PERSON}
          key={person.id}
          primary={person.id === primaryId}
        />
      ))}
      {hiddenCount > 0 && (
        <div className={styles.more}>
          {intl.formatMessage(messages.morePeople, { count: hiddenCount })}
        </div>
      )}
      <EntityLink entity={activity.deal} entityType={EntityType.DEAL} />
    </Section>
  );
};

const mapStateToProps = (state: RootState) => ({
  isMultiContactEnabled: doesOrganizationSupportMultiContactActivities(state),
});

export default connect(mapStateToProps)(RelationShipsSection);
